/**
 * §6.8 and §9.3 — the invoices this business has issued, and what is still owed.
 *
 * WHY THIS SCREEN EXISTS (2026-09-14)
 *
 * Invoices had no list. One was created from New Entry and then sat in Entries
 * among the bank transactions; Matching showed only posted, unpaid ones, and
 * only as targets for a deposit. So "which invoices are drafts, which are paid,
 * and when" took a database query to answer -- which is exactly what cleaning
 * up the Synergy Computer receivables took.
 *
 * WHAT IT IS NOT: an invoicing tool. §6.8 -- the app records invoices issued
 * elsewhere and does not generate or send them.
 *
 * THE AGING SUMMARY is `GET /invoices/open`'s totals, the same read Matching
 * uses, not a sum of the rows on screen: the list pages and filters, and a
 * total describing only the visible rows is wrong in a way that looks right.
 * Age is days since the invoice date. No payment terms are recorded, so
 * nothing here says "overdue".
 */

import { useCan } from '../auth/AuthProvider.js';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { InfoNote } from '../components/InfoNote.js';
import { ReceiptFrame } from '../components/ReceiptOverlay.js';
import { ReceivablesTabs } from '../components/ReceivablesTabs.js';
import { formatCents } from '../lib/money.js';
import {
  ApiError,
  api,
  type AgingBucket,
  type InvoiceFilter,
  type InvoiceStatus,
  type Attachment,
  type InvoiceRow,
  type InvoicesResult,
  type OpenInvoicesResult,
} from '../api.js';

const FILTERS: Record<InvoiceFilter, string> = {
  all: 'All',
  draft: 'Draft',
  open: 'Open',
  paid: 'Paid',
};

const STATUS_LABEL: Record<InvoiceStatus, string> = {
  draft: 'Draft',
  open: 'Open',
  partly_paid: 'Partly Paid',
  paid: 'Paid',
};

const BUCKETS: Array<{ key: AgingBucket; label: string }> = [
  { key: '0-30', label: '0–30 Days' },
  { key: '31-60', label: '31–60 Days' },
  { key: '61-90', label: '61–90 Days' },
  { key: '90+', label: 'Over 90 Days' },
  { key: 'future', label: 'Dated Ahead' },
];

function isFilter(value: string | null): value is InvoiceFilter {
  return value !== null && value in FILTERS;
}

export function InvoicesPage() {
  // What this screen offers (0069); the API refuses the rest either way.
  const can = useCan();
  const mayEdit = can('edit_entries');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('status');
  const filter: InvoiceFilter = isFilter(requested) ? requested : 'all';

  const [search, setSearch] = useState('');
  const [list, setList] = useState<InvoicesResult | null>(null);
  const [aging, setAging] = useState<OpenInvoicesResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  /* Bumped after terms are recorded; see the effect's dependency list. */
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api
      // Pledges are on the giving report; the forecast and To Do keep them (0084, DO-3).
      .listInvoices({ status: filter, search: search.trim() || undefined, invoicesOnly: true })
      .then((result) => {
        if (cancelled) return;
        setList(result);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (!cancelled) setError(caught instanceof ApiError ? caught.message : 'Could not load the invoices.');
      });
    return () => {
      cancelled = true;
    };
    /*
     * `reloadToken` is bumped after terms are recorded. The list is fetched by
     * an effect keyed on the filter and the search, and recording a due date
     * changes neither -- without a third key the row would keep showing "Add
     * Terms" until something else happened to refetch.
     */
  }, [filter, search, reloadToken]);

  // Once: the totals do not depend on the tab or the search.
  useEffect(() => {
    api
      .listOpenInvoices({ limit: 1, invoicesOnly: true })
      .then(setAging)
      .catch((caught: unknown) =>
        setError(caught instanceof ApiError ? caught.message : 'Could not load the aging totals.'),
      );
  }, []);

  function choose(next: InvoiceFilter) {
    const params = new URLSearchParams(searchParams);
    if (next === 'all') params.delete('status');
    else params.set('status', next);
    setSearchParams(params, { replace: true });
  }

  const countFor = (key: InvoiceFilter): number | null => {
    if (!list) return null;
    const { draft, open, partly_paid: partly, paid } = list.counts;
    if (key === 'all') return draft + open + partly + paid;
    if (key === 'open') return open + partly;
    return list.counts[key];
  };

  /**
   * The invoice whose terms are being recorded, with the receipt it was read
   * from. `attachment` is null while the receipt list is still loading, and
   * stays null when the invoice has none -- the date can still be typed, it
   * just has nothing to read it off.
   */
  const [terms, setTerms] = useState<{
    invoice: InvoiceRow;
    attachment: Attachment | null;
    date: string;
  } | null>(null);
  const [termsError, setTermsError] = useState<string | null>(null);

  async function openTerms(invoice: InvoiceRow) {
    setTermsError(null);
    setTerms({ invoice, attachment: null, date: '' });
    try {
      const { attachments } = await api.listAttachments(invoice.headerId);
      /*
       * The first attachment, which on a consulting invoice is the invoice
       * itself. Picking one rather than offering a list: the operator is here
       * to read a date off a document, and a chooser in front of that is a
       * step between them and the thing they came for. The overlay's own
       * controls are there if it is the wrong one.
       */
      setTerms((current) =>
        current && current.invoice.headerId === invoice.headerId
          ? { ...current, attachment: attachments[0] ?? null }
          : current,
      );
    } catch (caught) {
      setTermsError(
        caught instanceof ApiError ? caught.message : 'Could not open that invoice.',
      );
    }
  }

  async function saveTerms() {
    if (!terms) return;
    /*
     * IT SAYS WHY RATHER THAN DOING NOTHING.
     *
     * This used to `return` on an empty date, and the button was disabled in
     * the same condition -- so an operator whose date had not reached React
     * state pressed Save and got silence: no request, no refusal, no closed
     * dialog. "Nothing happens" is the hardest failure to report and the
     * hardest to diagnose, and it was the only silent path in this flow.
     *
     * The button is always pressable now. An empty date is answered with a
     * sentence, which also distinguishes "I did not type one" from "the one I
     * typed did not register".
     */
    if (terms.date === '') {
      setTermsError(
        'No due date is entered. Type or pick one in the Due field, then press Save Terms.',
      );
      return;
    }
    try {
      await api.setDueDate(terms.invoice.headerId, terms.date);
      setTerms(null);
      setTermsError(null);
      setReloadToken((token) => token + 1);
    } catch (caught) {
      // The server's sentence names both dates when the due date precedes the
      // entry date, which is what tells the operator which one is wrong.
      setTermsError(
        caught instanceof ApiError ? caught.message : 'Could not record those terms.',
      );
    }
  }

  return (
    <section className="invoices">
      <header className="row reports-head">
        <h1>Invoices</h1>
        {mayEdit && (
          <button type="button" onClick={() => navigate('/journals/new?kind=invoice')}>
            New Invoice
          </button>
        )}
      </header>

      <ReceivablesTabs />

      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}

      {aging && (
        <>
          <dl className="invoice-aging" aria-label="Receivables aging">
            <div>
              <dt>Outstanding</dt>
              <dd>{formatCents(aging.totalOutstandingCents)}</dd>
            </div>
            {BUCKETS.filter((bucket) => bucket.key !== 'future' || aging.byBucket.future > 0).map((bucket) => (
              <div key={bucket.key}>
                <dt>{bucket.label}</dt>
                <dd>{formatCents(aging.byBucket[bucket.key])}</dd>
              </div>
            ))}
          </dl>
          <p className="muted">
            Posted invoices still owed, by days since the invoice date as of {aging.asOf}. No payment
            terms are recorded, so this is age, not lateness.
          </p>
        </>
      )}

      <nav className="row tabs" aria-label="Invoice status">
        {(Object.keys(FILTERS) as InvoiceFilter[]).map((key) => (
          <button
            key={key}
            type="button"
            className={`tab${filter === key ? ' is-current' : ''}`}
            aria-current={filter === key}
            onClick={() => choose(key)}
          >
            {FILTERS[key]}
            {countFor(key) !== null && <span className="muted"> {countFor(key)}</span>}
          </button>
        ))}
      </nav>

      <div className="row triage-filters">
        <label>
          Search{' '}
          <input
            type="search"
            aria-label="Search invoices"
            placeholder="customer or invoice number"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
      </div>

      {list === null ? (
        !error && <p className="muted">Loading…</p>
      ) : list.invoices.length === 0 ? (
        <p className="muted">No invoices match.</p>
      ) : (
        <div className="table-scroll">
          <table className="lines invoice-list">
            <thead>
              <tr>
                <th>Invoice Number</th>
                <th>Customer</th>
                <th>Date</th>
                <th className="num">Amount</th>
                <th className="num">Paid</th>
                <th className="num">Outstanding</th>
                <th>Due</th>
                <th>Paid Date</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.invoices.map((invoice) => (
                <tr key={invoice.detailId}>
                  <td>
                    <Link to={`/journals/${invoice.headerId}`}>{invoice.invoiceReference ?? 'No Number'}</Link>
                  </td>
                  <td>{invoice.customer ?? '—'}</td>
                  <td className="date-cell">{invoice.entryDate}</td>
                  <td className="num">{formatCents(invoice.amountCents)}</td>
                  <td className="num">{invoice.paidCents > 0 ? formatCents(invoice.paidCents) : ''}</td>
                  {/*
                    OUTSTANDING. This cell went missing when the Due column was
                    added (migration 0036's screen work) and the table then had
                    ten headers over nine cells, so everything from here right
                    shifted one column left: "Add Terms" rendered under
                    Outstanding, the paid date under Due, the status under Paid
                    Date. The aging screen's most important number -- what is
                    still owed -- was not displayed at all, and the totals above
                    the table were right, which is what made it survive.

                    The row test asserts every cell BY POSITION now. `getByText`
                    inside a row passes happily against a shifted table.
                  */}
                  <td className="num">{formatCents(invoice.outstandingCents)}</td>
                  {/*
                    §6.1 — when it is due, and an offer to say so when nobody
                    has.

                    The terms are printed on the invoice PDF, which is already
                    attached and already renders in the receipt overlay. So the
                    backfill happens HERE rather than in a settings screen or a
                    script: the row that is missing a due date is the row that
                    offers to open its own PDF and take one.

                    An invoice with no due date shows no date. It is never
                    given its entry date, and never shown as due today -- see
                    migration 0036 on why those are different answers.
                  */}
                  <td className="due-cell">
                    {invoice.dueDate ?? (mayEdit && (
                      <button
                        type="button"
                        className="link"
                        aria-label={`Add a due date for ${invoice.invoiceReference ?? 'this invoice'}`}
                        onClick={() => void openTerms(invoice)}
                      >
                        Add Due Date
                      </button>
                    ))}
                  </td>
                  <td className="date-cell">{invoice.paidDate ?? ''}</td>
                  <td>
                    {STATUS_LABEL[invoice.status]}
                    {invoice.bucket && <span className="muted"> · {invoice.bucket} days</span>}
                  </td>
                  <td>
                    {/* Only while money is owed, and only on a posted invoice: a draft cannot be matched. */}
                    {(invoice.status === 'open' || invoice.status === 'partly_paid') && (
                      <Link
                        to={`/clearings?${new URLSearchParams(
                          invoice.customer ? { customer: invoice.customer } : {},
                        ).toString()}`}
                      >
                        Match a Payment
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {terms !== null && (
        <div className="overlay-backdrop" onClick={() => setTerms(null)}>
          <div
            className="overlay-panel terms-panel"
            role="dialog"
            aria-modal="true"
            aria-label={`Terms for ${terms.invoice.invoiceReference ?? 'this invoice'}`}
            onClick={(event) => event.stopPropagation()}
          >
            <header className="row overlay-head">
              <h2>
                {terms.invoice.invoiceReference ?? 'Invoice'} · {terms.invoice.customer ?? 'No customer'}
              </h2>
              <button type="button" className="link" onClick={() => setTerms(null)}>
                Close
              </button>
            </header>

            {termsError && (
              <p role="alert" className="error">
                {termsError}
              </p>
            )}

            <div className="row terms-row">
              <label className="field">
                <span>Due</span>
                <input
                  type="date"
                  aria-label="Due date"
                  value={terms.date}
                  /*
                   * The value is read HERE, not inside the state updater. React
                   * may run an updater later than the event, and reading
                   * `event.target.value` from inside one is a documented way to
                   * capture the wrong value -- on a controlled input that
                   * leaves the typed text sitting in the DOM while state stays
                   * empty, which looks exactly like a screen that ignores you.
                   *
                   * `min` is deliberately gone. It added nothing the server
                   * does not already enforce -- 0036's trigger refuses a due
                   * date before the entry date and says so with both dates in
                   * the sentence -- while giving the browser a reason to treat
                   * a typed value as out of range and report it as empty.
                   */
                  onChange={(event) => {
                    const { value } = event.target;
                    setTerms((current) => (current ? { ...current, date: value } : current));
                    if (value !== '') setTermsError(null);
                  }}
                />
              </label>
              {/* Beside the field it acts on, which is where it was looked for. */}
              <button type="button" onClick={() => void saveTerms()}>
                Save Terms
              </button>
              <InfoNote label="Where to find the due date">
                Issued {terms.invoice.entryDate}. The terms are printed on the invoice below —
                read the due date off it rather than working it out, and nothing here guesses
                one from the issue date.
              </InfoNote>
            </div>

            {/*
              The invoice itself, in the same frame the receipt overlay uses. If
              it has no attachment there is nothing to read from and the date can
              still be typed -- an invoice whose PDF was never filed is exactly
              the one whose terms are hardest to remember.
            */}
            {terms.attachment === null ? (
              <p className="muted">
                No document is attached to this invoice, so there is nothing to read the
                terms from here. The date can still be entered.
              </p>
            ) : (
              <ReceiptFrame attachment={terms.attachment} />
            )}
          </div>
        </div>
      )}

      {list !== null && list.total > list.invoices.length && (
        <p className="notice notice-warn">
          Showing the newest {list.invoices.length} of {list.total}. Search to find an older one.
        </p>
      )}
    </section>
  );
}
